import { env } from '@/env'

import { api } from '@/lib/api'

import type {
	AvailableIntegration,
	ConnectorEventsConfiguration,
	EditIntegrationFormData,
	Integration,
	IntegrationDefinition,
	IntegrationDefinitionDTO,
	IntegrationDTO,
	IntegrationEventTypesDTO,
	ListedSourcesResponse,
	MicrosoftConsentURLResponse,
	NewIntegrationFormData,
	ValidateIntegrationFormData,
	VerifyMicrosoftConsentCallbackData,
} from './types'
import type { EventSchemaResponse } from '@/shared/opensearch/types/schema'

/**
 * Fetch available integrations for the current tenant
 * @param tenant - The tenant ID
 * @param signal - (Optional) Abort signal
 */
export const fetchAvailableIntegrations = async (
	tenant: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<Array<AvailableIntegration>>(
		`/api/tenants/${tenant}/available_integrations`,
		{
			signal,
		},
	)

	return data
}

/**
 * Fetch connected integrations for the current tenant
 * @param tenant - The tenant ID
 * @param signal - (Optional) Abort signal
 */
export const fetchTenantIntegrations = async (
	tenant: string,
	signal?: AbortSignal,
) => {
	const {
		data: { connectors },
	} = await api.get<IntegrationDTO>(`/api/tenants/${tenant}/connectors`, {
		signal,
	})

	return connectors
}

/**
 * Fetch the connectors of several tenants in one request: `all` in the path,
 * the tenants in `X-AIR-Request-Tenants`. Each connector keeps its `tenant`.
 * The API drops a tenant the user cannot read, and answers 403 when none is
 * left.
 * @param tenants - The tenant IDs
 * @param signal - (Optional) Abort signal
 */
export const fetchTenantsIntegrations = async (
	tenants: ReadonlyArray<string>,
	signal?: AbortSignal,
) => {
	const {
		data: { connectors },
	} = await api.get<IntegrationDTO>('/api/tenants/all/connectors', {
		signal,
		tenants: [...tenants],
	})

	return connectors
}

/**
 * Fetch details for a specific integration
 * @param tenant - The tenant ID
 * @param integration_id - The integration ID
 * @param signal - (Optional) Abort signal
 */
export const fetchIntegrationDetails = async (
	tenant: string,
	integration_id: string,
	signal?: AbortSignal,
): Promise<IntegrationDefinitionDTO> => {
	const { data } = await api.get<IntegrationDefinitionDTO>(
		`/api/tenants/${tenant}/connectors/${integration_id}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Fetch details for a specific integration
 * @param tenant - The tenant ID
 * @param integration_id - The integration ID
 * @param signal - (Optional) Abort signal
 */
export const fetchIntegrationEventTypes = async (
	tenant: string,
	integration_id: string,
	signal?: AbortSignal,
): Promise<IntegrationEventTypesDTO> => {
	const { data } = await api.get<IntegrationEventTypesDTO>(
		`/api/tenants/${tenant}/connectors/${integration_id}/event_types`,
		{
			signal,
		},
	)

	return data
}

/**
 * Fetch the definition for a specific integration
 * @param tenant - The tenant ID
 * @param integration_id - The integration ID
 * @param signal - (Optional) Abort signal
 */
export const fetchIntegrationDefinition = async (
	tenant: string,
	integration_id: string,
	signal?: AbortSignal,
): Promise<IntegrationDefinition> => {
	const { data } = await api.get<IntegrationDefinition>(
		`/api/tenants/${tenant}/available_integrations/${integration_id}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Get the Microsoft consent URL for the current tenant
 * @param tenant - The tenant ID
 * @param signal - (Optional) Abort signal
 */
export const getMicrosoftConsentURL = async (
	tenant: string,
	signal?: AbortSignal,
) => {
	const baseURL = `/api/tenants/${tenant}/microsoft/consent_url`
	const queryParams = new URLSearchParams()

	// For development purposes, we need to set the redirect URL to the local development server
	queryParams.set('redirect_url', env.VITE_MICROSOFT_FRONTEND_REDIRECT_URL)

	const { data } = await api.get<MicrosoftConsentURLResponse>(
		`${baseURL}${queryParams.size > 0 ? `?${queryParams.toString()}` : ''}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Patch integration event types
 * @param tenant - The tenant ID
 * @param integration_id - The integration ID
 * @param events_config - Complete snapshot of all event types (ADR 0004 — backend scheduler is all-or-nothing)
 * @param signal - (Optional) Abort signal
 */
export const patchIntegrationEventType = async (
	tenant: string,
	integration_id: string,
	events_config: ConnectorEventsConfiguration,
	signal?: AbortSignal,
): Promise<{ success: boolean }> => {
	if (!integration_id || events_config.event_types.length === 0)
		throw new Error('Integration ID and event types are required')

	const { data } = await api.patch<{ success: boolean }>(
		`/api/tenants/${tenant}/connectors/${integration_id}/event_types`,
		{ events_config },
		{
			signal,
		},
	)

	return data
}

/**
 * Create a new Integration
 * @param tenant - The tenant ID
 * @param integration_data - The integration data
 */
export const createIntegration = async (
	tenant: string,
	integration_data: NewIntegrationFormData,
) => {
	const {
		data: { connector },
	} = await api.put<IntegrationDefinitionDTO>(
		`/api/tenants/${tenant}/connectors`,
		{ ...integration_data, name: integration_data.name.trim() },
	)

	return connector
}

/**
 * Edit an Integration
 * @param tenant - The tenant ID
 * @param integration_id - The integration ID
 * @param integration_data - The integration data
 */
export const editIntegration = async (
	tenant: string,
	integration_id: string,
	integration_data: EditIntegrationFormData,
) => {
	const { data } = await api.patch<Integration>(
		`/api/tenants/${tenant}/connectors/${integration_id}`,
		integration_data.name !== undefined
			? { ...integration_data, name: integration_data.name.trim() }
			: integration_data,
	)

	return data
}

/**
 * Delete an Integration
 * @param tenant - The tenant ID
 * @param integration_id - The integration ID
 */
export const deleteIntegration = async (
	tenant: string,
	integration_id: string,
) => {
	const { data } = await api.delete<Integration>(
		`/api/tenants/${tenant}/connectors/${integration_id}`,
	)

	return data
}

/**
 * Validate an Integration
 * @param tenant - The tenant ID
 * @param integration_data - The integration data
 */
export const validateIntegration = async (
	tenant: string,
	integration_data: NewIntegrationFormData,
) => {
	const { data } = await api.post<ValidateIntegrationFormData>(
		`/api/tenants/${tenant}/validate_connector_configuration`,
		integration_data,
	)

	return data
}

/**
 * Verify a Microsoft consent callback
 * @param {VerifyMicrosoftConsentCallbackData} callback_data - The callback data
 * @returns {Promise<{ status: string; connector_id: string }>} The status and connector ID
 */
export const verifyMicrosoftConsentCallback = async (
	callback_data: VerifyMicrosoftConsentCallbackData,
) => {
	const { data } = await api.post<{ status: string; connector_id: string }>(
		`/api/auth/microsoft/consent`,
		callback_data,
	)

	return data
}

/**
 * Fetch the event schema for a connected integration's event type. Returns
 * `{ source_schema, normalized_schema? }`; consumers build their own field trees.
 *
 * @param tenant - The tenant ID
 * @param definition_id - Connector definition ID
 * @param event_type - Event type within the connector
 * @param signal - Optional AbortSignal
 */
export const fetchEventSchema = async (
	tenant: string,
	definition_id: string,
	event_type: string,
	signal?: AbortSignal,
): Promise<EventSchemaResponse> => {
	const { data } = await api.get<EventSchemaResponse>(
		`/api/tenants/${tenant}/available_integrations/${definition_id}/event_schemas/${event_type}/logs`,
		{
			signal,
		},
	)

	return data
}

/**
 * Every Listed Source the tenant has, cloud connectors and network listeners
 * alike, active or not.
 * @param tenant - The tenant ID
 * @param signal - Optional AbortSignal
 */
export const fetchListedSources = async (
	tenant: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<ListedSourcesResponse>(
		`/api/tenants/${tenant}/logs/sources`,
		{ signal },
	)

	return data
}
